import React from 'react';
import { Toaster } from 'react-hot-toast';
import useAutoUpdate from './hooks/useAutoUpdate';
import ScrollToTop from './components/ScrollToTop';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { CartProvider, CartContext } from './context/CartContext';
import Loader from './components/Loader';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import CategoryNav from './components/CategoryNav';
import Menu from './components/Menu';
import Cart from './components/Cart';
import Footer from './components/Footer';
import SizeModal from './components/SizeModal';
import QRCodeModal from './components/QRCodeModal';
import OfferModal from './components/OfferModal';
import { requestForToken } from './firebase';
import RedirectPage from './components/RedirectPage';
import NotificationDialog from './components/NotificationDialog';
import CouponRevealDialog from './components/CouponRevealDialog';
import Wizard from './components/Onboarding/Wizard';
import TrackOrderModal from './components/TrackOrderModal';
import AdminDashboard from './pages/AdminDashboard';
import Login from './pages/Login';
import SuperadminLogin from './pages/SuperadminLogin';
import SuperadminDashboard from './pages/SuperadminDashboard';
import ManageSaasPlans from './pages/ManageSaasPlans';
import AddCustomer from './pages/AddCustomer';
import SuperAnalytics from './pages/SuperAnalytics';
import ManageAdmins from './pages/ManageAdmins';
import PaymentHistory from './pages/PaymentHistory';
import ScanMenu from './pages/ScanMenu';
import GlobalNotifications from './pages/GlobalNotifications';
import ManageAnnouncements from './pages/ManageAnnouncements';
import SaasSettings from './pages/SaasSettings';
import SuperadminViewMenu from './pages/SuperadminViewMenu';
import SuperadminEditCustomer from './pages/SuperadminEditCustomer';
import ManageQRTemplates from './pages/ManageQRTemplates';
import ImageRequests from './pages/ImageRequests';
import ManageLocations from './pages/ManageLocations';
import ManageAds from './pages/ManageAds';
import ManageProjectLinks from './pages/ManageProjectLinks';
import ManageSponsors from './pages/ManageSponsors';
import ImportMenu from './pages/ImportMenu';
import SponsorPortal from './pages/SponsorPortal';
import SuperadminAdAnalytics from './pages/SuperadminAdAnalytics';
import CookieConsent from './components/CookieConsent';
import './styles/styles.css';
import './styles/custom-dialog.css';
import { DialogProvider } from './context/DialogContext';
import LandingPage from './pages/LandingPage';
import LandingLayout from './components/LandingLayout';
import StaffLogin from './pages/StaffLogin';
import KitchenDashboard from './pages/KitchenDashboard';
import DeliveryDashboard from './pages/DeliveryDashboard';
import WaiterDashboard from './pages/WaiterDashboard';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import RefundPolicy from './pages/legal/RefundPolicy';
import ContactPage from './pages/ContactPage';
import SuperadminLeads from './pages/SuperadminLeads';
import SuperadminReviews from './pages/SuperadminReviews';
import ManageSaasCoupons from './components/Superadmin/ManageSaasCoupons';

const BASE = 'https://menu.searchisend.in';

const RestaurantStatus = ({ status }) => {
    const statusColor = status.isOpen ? 'var(--green-accent)' : 'var(--red-accent)';

    return (
        <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'var(--card-bg-color)', fontWeight: 600, color: statusColor, borderBottom: '1px solid var(--border-color)' }}>
            {status.message}
        </div>
    );
};

const MarqueeBar = ({ text }) => {
    if (!text) return null;
    return (
        <div className="marquee-bar">
            <div className="marquee-content">
                <span>{text}</span>
                <span>{text}</span>
            </div>
        </div>
    );
};

const CustomDialog = ({ message, type, onClose, autoClose = true }) => {
    React.useEffect(() => {
        if (autoClose) {
            const timer = setTimeout(() => { onClose(); }, 3000);
            return () => clearTimeout(timer);
        }
    }, [onClose, autoClose]);

    return (
        <div className="dialog-overlay" onClick={onClose}>
            <div className={`dialog-content ${type}`} onClick={(e) => e.stopPropagation()}>
                <p>{message}</p>
                <button onClick={onClose} className="dialog-close-btn">&times;</button>
            </div>
        </div>
    );
};

const FloatingOfferButton = ({ onClick }) => {
    return (
        <button onClick={onClick} className="floating-offer-btn" title="View Offers">
            <i className="fas fa-tags"></i>
            <span>Offers</span>
        </button>
    );
};

const RestaurantMenuPage = () => {
    useAutoUpdate('menu');
    const { restaurantId } = useParams();
    const { dispatch, ActionTypes, favorites, setActiveRestaurantId } = React.useContext(CartContext);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (setActiveRestaurantId) {
            setActiveRestaurantId(restaurantId);
        }
    }, [restaurantId, setActiveRestaurantId]);

    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [restaurantInfo, setRestaurantInfo] = React.useState(null);
    const [restaurantStatus, setRestaurantStatus] = React.useState({ isOpen: true, message: 'Loading...' });
    const [menu, setMenu] = React.useState([]);
    const [ads, setAds] = React.useState([]);
    const [fingerprint, setFingerprint] = React.useState('');
    const [offerImages, setOfferImages] = React.useState([]);
    const [coupons, setCoupons] = React.useState([]);
    const [activeCategory, setActiveCategory] = React.useState('');

    const [loyaltySuccessData, setLoyaltySuccessData] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [showOnlyVeg, setShowOnlyVeg] = React.useState(true);
    const [showOnlyFavorites, setShowOnlyFavorites] = React.useState(false);
    const [isCartOpen, setIsCartOpen] = React.useState(false);
    const [isSizeModalOpen, setIsSizeModalOpen] = React.useState(false);
    const [isQRModalOpen, setIsQRModalOpen] = React.useState(false);
    const [qrModalAmount, setQrModalAmount] = React.useState(null);
    const [qrModalOrder, setQrModalOrder] = React.useState(null); // Added for advance payment context
    const [itemForSizeModal, setItemForSizeModal] = React.useState(null);
    const [dialog, setDialog] = React.useState({ isOpen: false, message: '', type: '', autoClose: true });
    const [isOfferModalOpen, setIsOfferModalOpen] = React.useState(false);
    const [showNotificationDialog, setShowNotificationDialog] = React.useState(false);
    const [showCouponDialog, setShowCouponDialog] = React.useState(false);
    const [isTrackOrderOpen, setIsTrackOrderOpen] = React.useState(false);
    const [activeOrdersCount, setActiveOrdersCount] = React.useState(0);
    const [isServiceModalOpen, setIsServiceModalOpen] = React.useState(false);
    const [serviceIdentifier, setServiceIdentifier] = React.useState(localStorage.getItem('smart_menu_service_identifier') || '');
    const [showServicePrompt, setShowServicePrompt] = React.useState(false);
    const [pendingServiceType, setPendingServiceType] = React.useState('');
    const [tempIdentifier, setTempIdentifier] = React.useState('');
    const headerRef = React.useRef(null);
    
    // Parse table number from URL (e.g. ?table=4)
    const searchParams = new URLSearchParams(window.location.search);
    const tableNumber = searchParams.get('table') || 'Takeaway';
    const isWaiterMode = searchParams.get('waiterMode') === 'true';

    const checkActiveOrders = React.useCallback(() => {
        if (restaurantInfo?.id) {
            const stored = localStorage.getItem('smart_menu_active_orders');
            if (stored) {
                try {
                    const orders = JSON.parse(stored);
                    const count = orders.filter(o => o.restaurant_id === restaurantInfo.id).length;
                    setActiveOrdersCount(count);
                } catch (e) {}
            }
        }
    }, [restaurantInfo]);

    React.useEffect(() => {
        checkActiveOrders();
    }, [checkActiveOrders, isTrackOrderOpen]);

    React.useEffect(() => {
        const updateHeaderHeight = () => {
            if (headerRef.current) {
                document.documentElement.style.setProperty('--header-height', `${headerRef.current.offsetHeight}px`);
            }
        };
        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, [restaurantInfo, ads, restaurantStatus]);

    React.useEffect(() => {
        let fp = localStorage.getItem('menu_fingerprint');
        if (!fp) {
            fp = 'fp_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
            localStorage.setItem('menu_fingerprint', fp);
        }
        setFingerprint(fp);

        const fetchAds = async () => {
            try {
                const fd = new FormData();
                fd.append('action', 'fetch_ads');
                fd.append('restaurant_id', restaurantId);
                fd.append('fingerprint', fp);
                const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_public_ads.php`, { method: 'POST', body: fd });
                const data = await res.json();
                if (data.success) {
                    setAds(data.ads || []);
                }
            } catch (err) {}
        };
        fetchAds();

        const fetchMenuData = async () => {
            try {
                const logoResponse = await fetch(`https://menu.searchisend.in/apimenu/getLogo.php?id=${restaurantId}`);
                const logoData = await logoResponse.json();
                if (logoData.logo_url) {
                    const favicon = document.getElementById('dynamic-favicon');
                    if (favicon) {
                        favicon.href = logoData.logo_url;
                    }
                }

                const menuResponse = await fetch(`https://menu.searchisend.in/apimenu/getMenu.php?id=${restaurantId}`);
                const data = await menuResponse.json();

                if (data.error) {
                    setError(data.error);
                } else {
                    setShowOnlyVeg(data.restaurant.veg_filter_default_on === '1');
                    document.title = data.restaurant.restaurant_name + " - Digital Menu";
                    setRestaurantInfo(data.restaurant);
                    setCoupons(data.coupons || []);
                    
                    if (data.restaurant.theme_color) {
                        document.documentElement.style.setProperty('--primary-orange', data.restaurant.theme_color);
                    }
                    
                    if (data.restaurant.id && data.restaurant.username) {
                        if ('Notification' in window && Notification.permission === 'default' && !localStorage.getItem('notification_prompted')) {
                          setTimeout(() => {
                            setShowNotificationDialog(true);
                            localStorage.setItem('notification_prompted', 'true');
                          }, 7000);
                        }
                    }

                    // --- THIS IS THE NEW, CORRECTED LOGIC ---
                    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                    const now = new Date();
                    const todayIndex = now.getDay();
                    const todayName = days[todayIndex];

                    const todayOpenTime = data.restaurant[`open_time_${todayName}`];
                    const todayCloseTime = data.restaurant[`close_time_${todayName}`];
                    const isClosedToday = data.restaurant[`is_closed_${todayName}`];

                    if (isClosedToday || !todayOpenTime || !todayCloseTime) {
                        setRestaurantStatus({ isOpen: false, message: 'Currently Closed' });
                    } else {
                        const [openHour, openMinute] = todayOpenTime.split(':').map(Number);
                        const [closeHour, closeMinute] = todayCloseTime.split(':').map(Number);

                        const openTime = new Date();
                        openTime.setHours(openHour, openMinute, 0);

                        const closeTime = new Date();
                        closeTime.setHours(closeHour, closeMinute, 0);

                        if (now >= openTime && now < closeTime) {
                            const closeTimeString = closeTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            setRestaurantStatus({ isOpen: true, message: `Open until ${closeTimeString}` });
                        } else {
                            const openTimeString = openTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            setRestaurantStatus({ isOpen: false, message: `Closed. Opens at ${openTimeString}` });
                        }
                    }
                    // --- END OF NEW LOGIC ---

                    const categorizedMenu = data.menu.reduce((acc, item) => {
                        const categoryName = item.category || "Menu";
                        let category = acc.find(c => c.name === categoryName);
                        if (!category) { category = { name: categoryName, items: [] }; acc.push(category); }
                        category.items.push(item);
                        return acc;
                    }, []);
                    setMenu(categorizedMenu);
                    if (categorizedMenu.length > 0) setActiveCategory(categorizedMenu[0].name);
                    
                    if (data.offers && data.offers.length > 0) {
                        setOfferImages(data.offers);
                        setIsOfferModalOpen(true);
                    }
                }
            } catch (err) {
                console.error("Fetch Error:", err);
                setError("Could not load menu. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchMenuData();
    }, [restaurantId]);
    
    const showDialog = (message, type, autoClose = true) => setDialog({ isOpen: true, message, type, autoClose });
    const handleAddToCart = (itemToAdd, successCallback) => { if (itemToAdd.sizes && itemToAdd.sizes.length > 1) { setItemForSizeModal(itemToAdd); setIsSizeModalOpen(true); return; } if (itemToAdd.sizes && itemToAdd.sizes.length === 1) { const singleSize = itemToAdd.sizes[0]; const formattedItem = { ...itemToAdd, price: singleSize.price, sizes: undefined }; dispatch({ type: ActionTypes.ADD_TO_CART, payload: formattedItem }); if (successCallback) successCallback(); return; } dispatch({ type: ActionTypes.ADD_TO_CART, payload: itemToAdd }); if (successCallback) successCallback(); };
    
    const handleCheckout = async (orderDetails, method) => {
        const orderPhoneNumber = restaurantInfo?.phone_for_orders;
        if (!orderPhoneNumber && method === 'whatsapp') return showDialog("This restaurant has not configured a number for orders.", "error");
        if (orderDetails.items.length === 0) return showDialog("Your cart is empty.", "error");
        if (!orderDetails.customerName?.trim()) return showDialog("Please enter your name.", "error");
        if (!orderDetails.customerPhone?.trim()) return showDialog("Please enter your phone number.", "error");
        if (orderDetails.orderType === 'dine-in' && !orderDetails.tableNumber) return showDialog("Please select a table number.", "error");
        
        const homeDeliveryEnabled = String(restaurantInfo?.is_delivery_enabled) === '1';
        if (homeDeliveryEnabled && orderDetails.orderType === 'home-delivery' && !orderDetails.deliveryAddress?.trim()) return showDialog("Please enter your delivery address.", "error");

        if (method === 'online' || method === 'cash') {
            // Trigger software order saving to db
            try {
                const payload = {
                    customer_id: restaurantInfo.id,
                    table_number: orderDetails.orderType === 'dine-in' ? orderDetails.tableNumber : '',
                    order_type: orderDetails.orderType === 'home-delivery' ? 'Delivery' : (orderDetails.orderType === 'dine-in' ? 'Dine-in' : 'Takeaway'),
                    items: orderDetails.items,
                    total_amount: orderDetails.total,
                    customerName: orderDetails.customerName,
                    customerPhone: orderDetails.customerPhone, // ADDED THIS LINE
                    deliveryAddress: orderDetails.deliveryAddress,
                    customerMessage: orderDetails.customerMessage,
                    subtotal: orderDetails.subtotal,
                    gst: orderDetails.gst,
                    deliveryFee: orderDetails.deliveryFee,
                    discountAmount: orderDetails.discountAmount,
                    appliedCoupon: orderDetails.appliedCoupon,
                    loyaltyPin: orderDetails.loyaltyPin || '',
                    loyaltyPointsRedeemed: orderDetails.loyaltyPointsRedeemed || 0,
                    loyaltyDiscount: orderDetails.loyaltyDiscount || 0,
                    payment_mode: method // method now receives 'online' or 'cash'
                };

                const response = await fetch('https://menu.searchisend.in/apimenu/save_order.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                
                const data = await response.json();
                if (data.status === 'success') {
                    // Save to local storage for tracking
                    if (data.order_id && data.order_no) {
                        const stored = localStorage.getItem('smart_menu_active_orders');
                        let activeOrders = [];
                        if (stored) {
                            try { activeOrders = JSON.parse(stored); } catch (e) {}
                        }
                        activeOrders.push({
                            id: data.order_id,
                            order_no: data.order_no,
                            restaurant_id: restaurantInfo.id,
                            time: new Date().getTime()
                        });
                        localStorage.setItem('smart_menu_active_orders', JSON.stringify(activeOrders));
                        checkActiveOrders();
                    }

                    if (data.loyalty) {
                        if (data.loyalty.is_new) {
                            localStorage.setItem('smart_menu_loyalty_pin', data.loyalty.pin);
                        }
                    }
                    // Save info so they don't have to fill it again
                    localStorage.setItem('smart_menu_customer_name', orderDetails.customerName);
                    if (orderDetails.customerPhone) {
                        localStorage.setItem('smart_menu_customer_phone', orderDetails.customerPhone);
                    }

                    let successMessage = "Thank you! Your order has been placed.";
                    let isLoyalty = false;
                        if (data.loyalty && data.loyalty.is_new) {
                            isLoyalty = true;
                            successMessage = (
                                <div style={{ textAlign: 'left', lineHeight: '1.4' }}>
                                    <div style={{ marginBottom: '15px' }}>Thank you! Your order has been placed.</div>
                                    <div style={{ background: '#fff8f1', border: '1px dashed #ea580c', padding: '15px', borderRadius: '8px', color: '#ea580c', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>🎉 <strong>HOORAY! You're enrolled in our Loyalty Program!</strong></div>
                                        <div style={{ color: '#1e293b' }}>Your Secret PIN:</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '4px', margin: '5px 0' }}>{data.loyalty.pin}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>(Keep this safe to redeem {data.loyalty.points_earned} points on your next order!)</div>
                                    </div>
                                </div>
                            );
                        } else if (data.loyalty) {
                            isLoyalty = true;
                            successMessage = (
                                <div style={{ textAlign: 'left', lineHeight: '1.4' }}>
                                    <div style={{ marginBottom: '15px' }}>Thank you! Your order has been placed.</div>
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px', color: '#1e293b', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--primary-orange)', fontWeight: 'bold' }}>Loyalty Update</div>
                                        <div style={{ marginBottom: '5px' }}>Points Earned this order: <strong>{Number(data.loyalty.points_earned).toFixed(0)}</strong></div>
                                        <div>New Balance: <strong>{Number(data.loyalty.total_points).toFixed(0)} points</strong></div>
                                    </div>
                                </div>
                            );
                        }
                        showDialog(successMessage, "success", !isLoyalty);
                        dispatch({ type: ActionTypes.CLEAR_CART });
                        setIsTrackOrderOpen(true);
                    
                } else {
                    console.error("Order save error:", data.message);
                    return showDialog(data.message || "Error placing order. Please try again.", "error");
                }
            } catch (err) {
                console.error("Failed to save order:", err);
                return showDialog("Connection error. Could not place order.", "error");
            }
        } else if (method === 'whatsapp') {
            const phone = orderPhoneNumber || (restaurantInfo?.social_whatsapp ? restaurantInfo.social_whatsapp.replace(/[^0-9]/g, '') : null);
            if (!phone) {
                return showDialog("This restaurant has not configured a WhatsApp number for orders.", "error");
            }

            let msg = `*New Order:*\n`;
            msg += `Type: ${orderDetails.orderType}\n`;
            if (orderDetails.customerName) msg += `Name: ${orderDetails.customerName}\n`;
            if (orderDetails.customerPhone) msg += `Phone: ${orderDetails.customerPhone}\n`;
            if (orderDetails.orderType === 'dine-in' && orderDetails.tableNumber) msg += `Table No: ${orderDetails.tableNumber}\n`;
            if (orderDetails.orderType === 'home-delivery' && orderDetails.deliveryAddress) msg += `Address: ${orderDetails.deliveryAddress}\n`;
            if (orderDetails.customerMessage) msg += `Notes: ${orderDetails.customerMessage}\n`;
            
            msg += `\n*Items:*\n`;
            orderDetails.items.forEach(item => {
                const variantText = item.size ? ` (${item.size})` : '';
                msg += `- ${item.quantity}x ${item.name}${variantText} (₹${item.price})\n`;
            });

            if (orderDetails.discountAmount > 0) msg += `\nDiscount: -₹${orderDetails.discountAmount.toFixed(2)}`;
            if (orderDetails.loyaltyDiscount > 0) msg += `\nLoyalty Discount: -₹${orderDetails.loyaltyDiscount.toFixed(2)}`;
            msg += `\n*Total:* ₹${orderDetails.total.toFixed(2)}`;

            const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
            
            // Optionally save customer info locally
            localStorage.setItem('smart_menu_customer_name', orderDetails.customerName);
            if (orderDetails.customerPhone) {
                localStorage.setItem('smart_menu_customer_phone', orderDetails.customerPhone);
            }

            showDialog("Redirecting to WhatsApp...", "success");
            dispatch({ type: ActionTypes.CLEAR_CART });
            window.open(url, '_blank');
            setIsCartOpen(false);
        }
    };

    const handleAllowNotifications = async () => {
      setShowNotificationDialog(false);
      const permissionGranted = await requestForToken(restaurantInfo.id, restaurantInfo.username);

      if (permissionGranted) {
        if (restaurantInfo.notification_coupon_code) {
          setShowCouponDialog(true);
        } else {
          showDialog("Thank you for subscribing!", "success");
        }
      }
    };

    const executeServiceRequest = async (type, table_or_name) => {
        try {
            const res = await fetch(`${BASE}/apimenu/save_service_request.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    restaurant_id: restaurantInfo.id,
                    table_number: table_or_name,
                    request_type: type
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                showDialog("Service request sent to staff! Someone will be with you shortly.", "success");
            } else {
                showDialog(data.message || "Failed to send request.", "error");
            }
        } catch (error) {
            console.error(error);
            showDialog("Network error. Please try again.", "error");
        }
    };

    const handleServiceRequest = (type) => {
        setIsServiceModalOpen(false);
        if (tableNumber === 'Takeaway' && !serviceIdentifier) {
            setPendingServiceType(type);
            setShowServicePrompt(true);
            return;
        }
        
        const finalTableNumber = (tableNumber === 'Takeaway' && serviceIdentifier) ? serviceIdentifier : tableNumber;
        executeServiceRequest(type, finalTableNumber);
    };

    const submitServicePrompt = () => {
        if (!tempIdentifier.trim()) {
            showDialog("Please enter your table number or name.", "error");
            return;
        }
        const trimmedIdentifier = tempIdentifier.trim();
        setServiceIdentifier(trimmedIdentifier);
        localStorage.setItem('smart_menu_service_identifier', trimmedIdentifier);
        setShowServicePrompt(false);
        executeServiceRequest(pendingServiceType, trimmedIdentifier);
    };

    const handlePayWithQR = (orderOrAmount = null) => { 
        if (orderOrAmount !== null && typeof orderOrAmount === 'object' && orderOrAmount.order_type) {
            // It's an order object, calculate advance
            const order = orderOrAmount;
            let advancePercent = 0;
            if (order.order_type === 'Dine-in') advancePercent = parseFloat(restaurantInfo?.advance_dinein_percent || 0);
            else if (order.order_type === 'Takeaway') advancePercent = parseFloat(restaurantInfo?.advance_takeaway_percent || 0);
            else if (order.order_type === 'Delivery') advancePercent = parseFloat(restaurantInfo?.advance_delivery_percent || 0);
            
            const total = parseFloat(order.grand_total);
            const amount = advancePercent > 0 ? (total * (advancePercent / 100)) : total;
            
            setQrModalAmount(amount.toFixed(2));
            setQrModalOrder(order);
        } else {
            // Just a flat amount or null
            setQrModalAmount(orderOrAmount);
            setQrModalOrder(null);
        }
        setIsQRModalOpen(true); 
    };
    const getFilteredMenu = () => { if (!menu) return []; return menu.map(category => { let items = category.items; if (showOnlyVeg) items = items.filter(item => item.item_type === 'veg' || item.item_type === 'addon'); if (showOnlyFavorites) items = items.filter(item => favorites.includes(item.item_id)); if (searchTerm) items = items.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())); return { ...category, items }; }).filter(category => category.items.length > 0); };
    const filteredMenu = getFilteredMenu();

    if (isLoading) return <Loader />;
    if (error) return <div style={{textAlign: 'center', padding: '50px', color: 'var(--red-accent)'}}>{error}</div>;

    return (
        <div className="app-container">
            {restaurantStatus.isOpen === false && <RestaurantStatus status={restaurantStatus} />}
            {isWaiterMode && (
                <div style={{ background: '#1e293b', color: '#fff', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 102, position: 'relative' }}>
                    <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}><i className="fas fa-concierge-bell" style={{ color: 'var(--primary-orange)' }}></i> Waiter Mode</span>
                    <button 
                        onClick={() => navigate('/waiter-dashboard')} 
                        style={{ background: 'var(--primary-orange)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-arrow-left"></i> Back to Hub
                    </button>
                </div>
            )}
            <div className="sticky-header-container" ref={headerRef}>
                <MarqueeBar text={restaurantInfo?.announcement_text} />
                <Header 
                    restaurantInfo={restaurantInfo} 
                    onCartClick={() => setIsCartOpen(true)}
                    onPayClick={() => handlePayWithQR()}
                />
                <RestaurantStatus status={restaurantStatus} />
            </div>
            
            {dialog.isOpen && <CustomDialog message={dialog.message} type={dialog.type} onClose={() => setDialog({isOpen: false, message: '', type: '', autoClose: true})} autoClose={dialog.autoClose} />}
            {isOfferModalOpen && <OfferModal imageUrls={offerImages} onClose={() => setIsOfferModalOpen(false)} />}
            
            {loyaltySuccessData && (
                <div className="dialog-overlay" style={{ zIndex: 9999 }}>
                    <div className="dialog-content success" style={{ padding: '30px', textAlign: 'center', maxWidth: '400px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🎉</div>
                        <h2 style={{ margin: '0 0 10px 0', color: '#16a34a' }}>HOORAY!</h2>
                        <p style={{ fontSize: '1.1rem', marginBottom: '20px' }}>You just earned <strong>{loyaltySuccessData.points_earned} points</strong>!</p>
                        <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '2px dashed #cbd5e1', marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 5px 0', color: '#64748b', fontSize: '0.9rem' }}>Your Secret PIN for redemption:</p>
                            <h1 style={{ margin: 0, fontSize: '2.5rem', color: '#ea580c', letterSpacing: '4px' }}>{loyaltySuccessData.pin}</h1>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '25px' }}>
                            Please take a screenshot of this PIN. You will need it to redeem your points on future orders!
                        </p>
                        <button 
                            className="btn checkout-btn" 
                            style={{ backgroundColor: '#ea580c', width: '100%', padding: '12px', fontSize: '1.1rem' }}
                            onClick={() => setLoyaltySuccessData(null)}
                        >
                            Got It!
                        </button>
                    </div>
                </div>
            )}
            
            <div className="container main-content-area">
                <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
                <CategoryNav categories={menu.map(cat => cat.name)} activeCategory={activeCategory} onCategoryChange={(cat) => { setActiveCategory(cat); setSearchTerm(''); setShowOnlyFavorites(false); }} />
                <Menu
                    menuData={filteredMenu}
                    activeCategory={activeCategory}
                    onCategoryScroll={setActiveCategory}
                    onAddToCart={handleAddToCart}
                    showOnlyVeg={showOnlyVeg}
                    onVegToggle={() => setShowOnlyVeg(!showOnlyVeg)}
                    showOnlyFavorites={showOnlyFavorites}
                    onToggleFavorites={() => {
                        setShowOnlyFavorites(!showOnlyFavorites);
                        if (showOnlyFavorites && menu.length > 0) {
                            setActiveCategory(menu[0].name);
                        } else {
                            setActiveCategory('');
                        }
                    }}
                    searchTerm={searchTerm}
                    inMenuFirstAd={ads.find(a => a.placement_zone === 'in_menu_first')}
                    inMenuSecondAd={ads.find(a => a.placement_zone === 'in_menu_second')}
                    restaurantId={restaurantId}
                    fingerprint={fingerprint}
                />
            </div>
            
            <Cart
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                onCheckout={handleCheckout}
                onShowDialog={showDialog}
                homeDeliveryEnabled={String(restaurantInfo?.is_delivery_enabled) === '1'}
                dineInEnabled={String(restaurantInfo?.is_dine_in_enabled) === '1'}
                takeawayEnabled={String(restaurantInfo?.is_takeaway_enabled) !== '0'}
                whatsappEnabled={String(restaurantInfo?.is_whatsapp_ordering_enabled) === '1'}
                couponsEnabled={coupons.length > 0}
                coupons={coupons}
                restaurantInfo={restaurantInfo}
                isRestaurantOpen={restaurantStatus.isOpen}
                menuData={menu}
                onAddToCart={handleAddToCart}
                showOnlyVeg={showOnlyVeg}
                isWaiterMode={isWaiterMode}
                initialTableNumber={tableNumber === 'Takeaway' && serviceIdentifier ? serviceIdentifier : tableNumber}
            />
            
            {isSizeModalOpen && (
                <SizeModal 
                    item={itemForSizeModal}
                    onClose={() => setIsSizeModalOpen(false)}
                    onAddToCart={handleAddToCart}
                />
            )}
            {isQRModalOpen && (
                <QRCodeModal
                    onClose={() => setIsQRModalOpen(false)}
                    upiId={restaurantInfo?.upi_id}
                    payeeName={restaurantInfo?.upi_payee_name || restaurantInfo?.restaurant_name}
                    amount={qrModalAmount}
                    order={qrModalOrder}
                    restaurantPhone={restaurantInfo?.phone_for_orders}
                />
            )}

            {showNotificationDialog && restaurantInfo && (
              <NotificationDialog
                onAllow={handleAllowNotifications}
                onDeny={() => setShowNotificationDialog(false)}
                couponCodeExists={!!restaurantInfo.notification_coupon_code}
              />
            )}

            {showCouponDialog && (
              <CouponRevealDialog
                couponCode={restaurantInfo.notification_coupon_code}
                onClose={() => setShowCouponDialog(false)}
              />
            )}

            <Footer />
            
            {offerImages.length > 0 && <FloatingOfferButton onClick={() => setIsOfferModalOpen(true)} />}
            
            {activeOrdersCount > 0 && (
                <button 
                    onClick={() => setIsTrackOrderOpen(true)} 
                    style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '30px', padding: '12px 24px', fontSize: '1rem', fontWeight: 'bold', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', cursor: 'pointer', zIndex: 99, display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <i className="fas fa-route"></i> Track Order
                </button>
            )}

            {isTrackOrderOpen && (
                <TrackOrderModal 
                    onClose={() => setIsTrackOrderOpen(false)}
                    restaurantId={restaurantInfo.id}
                    onPayClick={(order) => {
                        setIsTrackOrderOpen(false);
                        handlePayWithQR(order);
                    }}
                />
            )}

            {/* Service Ping Speed Dial */}
            <div className={`service-ping-container ${isServiceModalOpen ? 'open' : ''}`}>
                {isServiceModalOpen && <div className="service-ping-backdrop" onClick={() => setIsServiceModalOpen(false)}></div>}
                
                <div className="service-ping-options">
                    <button onClick={() => handleServiceRequest('Water')} className="service-ping-option">
                        <span className="option-label">Water</span>
                        <div className="option-icon-wrapper bg-blue"><i className="fas fa-glass-water"></i></div>
                    </button>
                    <button onClick={() => handleServiceRequest('Waiter')} className="service-ping-option">
                        <span className="option-label">Waiter</span>
                        <div className="option-icon-wrapper bg-orange"><i className="fas fa-bell-concierge"></i></div>
                    </button>
                    <button onClick={() => handleServiceRequest('Bill')} className="service-ping-option">
                        <span className="option-label">Bill</span>
                        <div className="option-icon-wrapper bg-red"><i className="fas fa-file-invoice-dollar"></i></div>
                    </button>
                </div>
                
                <button 
                    onClick={() => setIsServiceModalOpen(!isServiceModalOpen)}
                    className="service-ping-fab"
                    title="Service Request"
                >
                    <i className={`fas ${isServiceModalOpen ? 'fa-times' : 'fa-concierge-bell'}`} style={{ transition: 'transform 0.3s' }}></i>
                </button>
            </div>
            {showServicePrompt && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ background: 'var(--bg-color)', padding: '24px', borderRadius: '12px', width: '90%', maxWidth: '350px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-color)' }}>Identify Yourself</h3>
                        <p style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: 'var(--text-color-secondary)' }}>Please enter your Table Number or Name so our staff knows where to go.</p>
                        <input 
                            type="text" 
                            placeholder="e.g. Table 5 or John" 
                            value={tempIdentifier}
                            onChange={(e) => setTempIdentifier(e.target.value)}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '20px', fontSize: '1rem', boxSizing: 'border-box', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)' }}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowServicePrompt(false)} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', background: 'transparent', borderRadius: '50px', cursor: 'pointer', color: 'var(--text-color)' }}>Cancel</button>
                            <button onClick={submitServicePrompt} style={{ padding: '8px 16px', border: 'none', background: 'var(--primary-orange)', color: '#fff', borderRadius: '50px', cursor: 'pointer', fontWeight: 'bold' }}>Submit</button>
                        </div>
                    </div>
                </div>
            )}

            <CookieConsent />
        </div>
    );
};

function App() {
  return (
    <DialogProvider>
      <CartProvider>
        <Toaster />
        <ScrollToTop />
        <Routes>
          <Route element={<LandingLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/signup" element={<Wizard />} />
              <Route path="/login" element={<Login />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/contact" element={<ContactPage />} />
          </Route>
          
          <Route path="/sponsor-portal" element={<SponsorPortal />} />
          
          {/* The obscured Admin V2 Route */}
          <Route path="/manage_v2_secure_auth/*" element={<AdminDashboard />} />

          {/* The Superadmin Routes */}
          <Route path="/superadmin_v2_secure/ad_analytics" element={<SuperadminAdAnalytics />} />
          <Route path="/superadmin_v2_secure_login" element={<SuperadminLogin />} />
          <Route path="/superadmin_v2_secure" element={<SuperadminDashboard />} />
          <Route path="/superadmin_v2_secure/view_menu/:customerId" element={<SuperadminViewMenu />} />
          <Route path="/superadmin_v2_secure/edit_customer/:customerId" element={<SuperadminEditCustomer />} />
          <Route path="/superadmin_v2_secure/add_customer" element={<AddCustomer />} />
          <Route path="/superadmin_v2_secure/analytics" element={<SuperAnalytics />} />
          <Route path="/superadmin_v2_secure/manage_superadmins" element={<ManageAdmins />} />
          <Route path="/superadmin_v2_secure/payment_history" element={<PaymentHistory />} />
          <Route path="/superadmin_v2_secure/scan_menu" element={<ScanMenu />} />
          <Route path="/superadmin_v2_secure/import_menu" element={<ImportMenu />} />
          <Route path="/superadmin_v2_secure/manage_notifications" element={<GlobalNotifications />} />
          <Route path="/superadmin_v2_secure/manage_announcements" element={<ManageAnnouncements />} />
          <Route path="/superadmin_v2_secure/manage_saas_settings" element={<SaasSettings />} />
          <Route path="/superadmin_v2_secure/manage_saas_plans" element={<ManageSaasPlans token={localStorage.getItem('saas_superadmin_token')} />} />
          <Route path="/superadmin_v2_secure/manage_saas_coupons" element={<ManageSaasCoupons />} />
          <Route path="/superadmin_v2_secure/manage_ads" element={<ManageAds />} />
          <Route path="/superadmin_v2_secure/manage_sponsors" element={<ManageSponsors />} />
          <Route path="/superadmin_v2_secure/manage_qr_templates" element={<ManageQRTemplates />} />
          <Route path="/superadmin_v2_secure/image_requests" element={<ImageRequests />} />
          <Route path="/superadmin_v2_secure/manage_locations" element={<ManageLocations />} />
          <Route path="/superadmin_v2_secure/leads" element={<SuperadminLeads />} />
          <Route path="/superadmin_v2_secure/reviews" element={<SuperadminReviews />} />
          <Route path="/superadmin_v2_secure/project_links" element={<ManageProjectLinks />} />
          
          {/* Kitchen & Delivery Staff Routes */}
          <Route path="/staff-login" element={<StaffLogin />} />
          <Route path="/kitchen-dashboard" element={<KitchenDashboard />} />
          <Route path="/delivery-dashboard" element={<DeliveryDashboard />} />
          <Route path="/waiter-dashboard" element={<WaiterDashboard />} />
          
          <Route path="/:restaurantId" element={<RestaurantMenuPage />} />
          <Route path="/redirect" element={<RedirectPage />} />
        </Routes>
      </CartProvider>
    </DialogProvider>
  );
}

export default App;
